import styled from '@emotion/styled'
import {device} from '../../styles/media-queries'

// Thin, quiet scrollbar for the app's scroll container (<main>). Standard
// properties first (Chrome 121+/Firefox), -webkit- pseudo-elements for older
// Chromium/Safari.
const thinScrollbar = `
  scrollbar-width: thin;
  scrollbar-color: #c3cdf0 transparent;
  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #c3cdf0;
    border: 3px solid transparent;
    border-radius: 10px;
    background-clip: padding-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: #a5b4e6;
  }
`

// Modals/drawers lock page scroll with `document.body.style.overflow =
// 'hidden'`. Now that <main> is the scroller, body overflow no longer locks it,
// so mirror that inline style onto main here (covers every locker, including
// third-party ones, without touching their call sites).
const scrollLockSelector = `body[style*='overflow: hidden'] &`

// <main> is a flex column so the in-flow <AppFooter> (margin-top: auto) rests at
// the bottom of short pages and follows the content on tall ones.
const stickyFooterMain = `
  display: flex;
  flex-direction: column;
  & > :not(footer) {
    flex-shrink: 0;
  }
`

export const AppContainer = styled.article`
  position: relative;
  isolation: isolate; // scopes AppBackground's z-index: -1 to the shell
  // Fixed-viewport shell: header/sidebar stay put and only <main> scrolls, so
  // the scrollbar starts below the header instead of running beside it.
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  display: grid;
  // gap: 2px;
  // main-content takes 1fr (fills whatever space is left after the fixed
  // header and content-sized footer) instead of the previous fixed-height
  // main forcing itself to calc(100vh - 195px) regardless of how much real
  // content there was — that stale magic number is what left a visible gap
  // above the footer on shorter pages (Account Management, Proposal Tool).
  grid-template-rows: 70px minmax(0, 1fr);
  grid-template-columns: ${props =>
    props.hideSidebar ? '0px' : props.isSidebarOpen ? '245px' : '64px'};
  grid-template-areas:
    'header header'
    'sidebar main-content';

  // No sidebar (Keap Partner landing): <main> starts at the very top so the
  // fixed header floats over the page content instead of above it.
  ${props =>
    props.hideSidebar
      ? `
  grid-template-rows: minmax(0, 1fr);
  grid-template-areas: 'main-content main-content';
  `
      : ''}

  @media (max-width: 835px) {
    grid-template-areas:
      'header header'
      'main-content main-content';
    ${props =>
      props.hideSidebar
        ? `grid-template-areas: 'main-content main-content';`
        : ''}
    & main {
      padding-left: 15px;
    }
  }

  & main {
    grid-area: main-content;
    min-width: 100%;
    min-height: 0;
    overflow-y: auto;
    z-index: 9;
    ${thinScrollbar}
  }
  & > main {
    ${stickyFooterMain}
  }
  ${scrollLockSelector} main {
    overflow-y: hidden;
  }
`
export const AppKeapPartnerContainer = styled.article`
  position: relative;
  isolation: isolate; // scopes AppBackground's z-index: -1 to the shell
  height: 100vh;
  display: grid;
  grid-template-rows: 64px minmax(0, 1fr);
  grid-template-areas:
    'header'
    'main-content';

  @media (max-width: 835px) {
    grid-template-areas:
      'header'
      'main-content';
  }

  // AppHeader is a transparent fixed bar (see app-shell/AppHeader.styles.js);
  // keep only the grid slot here so this rule doesn't paint it white.
  & header {
    grid-area: header;
    height: 64px;
  }

  & main {
    grid-area: main-content;
    min-width: 100%;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    ${thinScrollbar}
  }
  & > main {
    ${stickyFooterMain}
  }
  ${scrollLockSelector} main {
    overflow-y: hidden;
  }
`

export const OldAppContainer = styled.article`
  min-height: 100vh;
  min-height: 100dvh;
  display: grid;
  grid-template-areas:
    'header'
    'main-content';
  grid-template-columns: 100%;
  grid-template-rows: auto 1fr;
  & header {
    grid-area: header;
  }
  & main {
    grid-area: main-content;
    min-width: 100%;
    min-height: 0;
  }
  & > main {
    ${stickyFooterMain}
  }
  /* @media screen and ${device.tablet} {
  grid-template-rows: auto calc(100vh - 200px) auto;
} */
`
